// services/userService.ts
import axiosInstance from '../../services/http.api';
import axiosAdminInstance from '../../services/adminUser.api';
import { DEFAULT_ERROR_MSG, DEFAULT_LANGUAGE } from '../../utils/constants';
import { UserData, UserConstants, SiteData, LoggedInUser } from './user.types';
import { setSites } from './userSlice';


export const userService = {

  /**
   * Fetch users
   * @returns 
   */
  fetchUsers: async (loginUserData: LoggedInUser): Promise<UserData[]> => {
    try {

      if (!loginUserData.email) {
        throw new Error(DEFAULT_ERROR_MSG);
      }

      const customerSites = await userService.fetchCustomerSites();

      if (customerSites?.length > 0) {
          setSites(customerSites);
      }

      const res = await axiosAdminInstance.get<any>(`/v1/User/child/${loginUserData.email}`);

      if (!res.data.isSuccess) {
        throw new Error(res.data.message || UserConstants.ERROR_FETCH_USER);
      }

      let data = res.data.subAccounts || [];

      const users: UserData[] = data.map((user: any) => ({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        siteName: user.registrations?.map((registrationData: any) => registrationData?.data?.customerSites?.split(","))[0] || [],
        status: user.status,
        preferredLanguage : (user.preferredLanguages && user.preferredLanguages.length > 0) ? user.preferredLanguages[0] : DEFAULT_LANGUAGE
      }));
      return users;
    } catch (error: any) {
      console.log(error);
      throw new Error(error?.response?.data?.message || UserConstants.ERROR_FETCH_USER);
    }
  },

/**
 * Create user
 * @param user 
 * @param loginUserData 
 * @returns 
 */
  addUser: async (user: UserData, loginUserData: LoggedInUser): Promise<UserData> => {
    try {

      let customerSites = "";
      if (user.siteName?.length > 0) {
        customerSites = user.siteName.join(",");
      }

      const userData = { 'firstName': user.firstName, 'lastName': user.lastName, 'email': user.email, "customerSites": customerSites, "salesForceId": loginUserData?.salesforceId, "certificateId": null, "accountId": null, "accountName": "", "parentSalesForceId": loginUserData?.salesforceId, "parentAccountEmail": loginUserData?.email, "parentAccountId": null, "scsPortal": [12], "textilePortal": null, preferredLanguage: loginUserData.preferredLanguage };

      const res = await axiosAdminInstance.post<any>('/v1/LOGOPortal', userData);

      if (!res.data.isSuccess) {
        throw new Error(res.data.message || UserConstants.ERROR_ADD_USER);
      }

      let createdUserRes = res.data.user || {};

      const users: UserData = {
        id: createdUserRes.id,
        firstName: createdUserRes.firstName,
        lastName: createdUserRes.lastName,
        email: createdUserRes.email,
        siteName: createdUserRes.registrations?.map((registrationData: any) => registrationData?.data?.customerSites?.split(","))[0] || [],
        status: user.status,
        preferredLanguage: (createdUserRes.preferredLanguages && createdUserRes.preferredLanguages.length > 0) ? createdUserRes.preferredLanguages[0] : DEFAULT_LANGUAGE
      };

      return users;

    } catch (error: any) {
      throw new Error(error?.response?.data?.errors?.Message || UserConstants.ERROR_ADD_USER);
    }
  },

/**
 * Update User
 * @param user 
 * @param loginUserData 
 * @returns 
 */
  updateUser: async (user: UserData, loginUserData: LoggedInUser): Promise<UserData> => {
    try {
      let customerSites = "";
      if (user.siteName?.length > 0) {
        customerSites = user.siteName.join(",");
      }

      const userData = { 'firstName': user.firstName, 'lastName': user.lastName, 'email': user.email, "customerSites": customerSites, "salesForceId": loginUserData?.salesforceId, "certificateId": null, "accountId": null, "accountName": "", "parentSalesForceId": loginUserData?.salesforceId, "parentAccountEmail": loginUserData?.email, "parentAccountId": null, "scsPortal": [12], "textilePortal": null, preferredLanguage: user.preferredLanguage };

      const res = await axiosAdminInstance.put<any>('/v1/LOGOPortal', userData);

      if (!res.data.isSuccess) {
        throw new Error(res.data.message || UserConstants.ERROR_ADD_USER);
      }

      let createdUserRes = res.data.user || {};

      const users: UserData = {
        id: createdUserRes.id,
        firstName: createdUserRes.firstName,
        lastName: createdUserRes.lastName,
        email: createdUserRes.email,
        siteName: createdUserRes.registrations?.map((registrationData: any) => registrationData?.data?.customerSites?.split(","))[0] || [],
        status: user.status,
        preferredLanguage: (createdUserRes.preferredLanguages && createdUserRes.preferredLanguages.length > 0) ? createdUserRes.preferredLanguages[0] : DEFAULT_LANGUAGE
      };

      return users;
    } catch (error: any) {
      throw new Error(error?.response?.data?.errors?.Message || UserConstants.ERROR_UPDATE_USER);
    }
  },

  /**
   * Delete user
   * @param users 
   * @param deleteRegistrationOnly 
   * @returns 
   */
  deleteUser: async (users: UserData[], deleteRegistrationOnly: boolean): Promise<any> => {
    try {
      const res = await axiosAdminInstance.delete(`/v1/User/${users[0].id}`, {
        params: { deleteRegistrationOnly }
      });

      if (!res.data.isSuccess) {
        // Throw an error with custom message
        throw new Error(res.data.message || DEFAULT_ERROR_MSG);
      }
      return users[0];
    } catch (error: any) {
      throw new Error(error?.response?.data?.errors?.Message || UserConstants.ERROR_DELETE_USER);
    }
  },

/**
 * Delete multiple user
 * @param users 
 * @param deleteRegistrationOnly 
 * @returns 
 */
  bulkDeleteUser: async (users: UserData[], deleteRegistrationOnly: boolean): Promise<any> => {
    try {
      const userIds = users.map(user => user.id);
      const payload = {
        userIds: userIds,
        deleteRegistrationOnly: deleteRegistrationOnly,
        maxConcurrency: 0,
      };

      const res = await axiosAdminInstance.delete("/v1/User/bulk", {
        data: payload
      });

      if (!res.data.isSuccess) {
        // Throw an error with custom message
        throw new Error(res.data.message || DEFAULT_ERROR_MSG);
      }
      return res.data.results;
    } catch (error: any) {
      throw new Error(error?.response?.data?.errors?.Message || UserConstants.ERROR_DELETE_USER);
    }
  },

  /**
    * Get sites
    * @returns 
    */
  fetchCustomerSites: async (): Promise<SiteData[]> => {
    try {

      const res = await axiosInstance.get<any>(`/v1/LOGORequest/customerSites`);

      if (!res.data.isSuccess) {
        // Throw an error with custom message
        throw new Error(res.data.message || DEFAULT_ERROR_MSG);
      }

      const data = res.data.certifiedSitesModels || [];
      let certificateSite: SiteData[] = data.map((siteItem: any) => ({
        id: siteItem?.customerSite?.id,
        label: siteItem?.customerSite?.name,
        value: siteItem?.customerSite?.name
      }));

      certificateSite = (certificateSite && certificateSite.length > 0) ? certificateSite : [{id:"N/A",label:"No data found",value:"N/A"}];

      return certificateSite;

    } catch (error: any) {
      throw new Error(error?.response?.data?.message || UserConstants.ERROR_FETCH_SITE);
    }
  }
}